/**
 * SavedConnectionsPanel — a library of saved connections for a project, so
 * entering a new one doesn't overwrite the last. Additive on top of the
 * existing single "active" slot each kind reads from — "Use This" pushes a
 * saved entry into that slot via the backend's activate endpoint; the rest
 * of Data Converter (schema scan, DDL, load, target-object load, ...) keeps
 * reading from that same slot unchanged. "source"/"target" activate into
 * admin/project_db_config.py's DataConnection slot; "crm" activates into
 * ConverterCrmConnection (the Target Platform / Salesforce-Dynamics-Power
 * Platform connection) instead — see activate_saved_connection in
 * service.py for the kind-based dispatch.
 *
 * For "source"/"target" (identical field shape — host/port/database/...),
 * the list returned by the backend is the UNION of both kinds, so a
 * connection saved on one DB step shows up on the other too (with a small
 * kind badge) instead of needing retyping — see list_saved_connections in
 * service.py. "crm" never merges with the DB kinds (different field
 * shape). A cross-kind row's action button reads "Use as {kind}" and passes
 * `asKind` through to the activate endpoint, which pushes the row's config
 * into the REQUESTED kind's slot server-side instead of the row's own kind
 * — the source and staging DB are often literally the same server, just a
 * different schema, so this is a real, common case, not just a convenience.
 *
 * Reused for the Source DB step, the Target Connection card (Create Tables
 * step), and the Target Platform (Salesforce/Dynamics/Power Platform) card
 * — parameterized by `kind`.
 */

import { useCallback, useEffect, useState } from "react";
import { Loader2, Database, Pencil, X, CheckCircle2 } from "lucide-react";
import {
  apiv2,
  type DbConnectionConfig,
  type DBConfigOut,
  type SalesforceConnectionConfig,
  type SavedConnectionOut,
} from "../apiv2";
import {
  savedConnectionDetail,
  savedConnectionEndpoint,
  savedConnectionTitle,
} from "../lib/savedConnectionLabel";

export default function SavedConnectionsPanel({
  projectId,
  kind,
  currentConn,
  onLoad,
  onActivated,
  onConnectExisting,
}: {
  projectId: string;
  kind: "source" | "target" | "crm";
  // Both optional together: when there's no live form on the page to save
  // from or load into (the "source"/"target" DB steps now connect via
  // ConnectDatabaseDialog's modal instead of a persistent form field set —
  // see DataConverterView's Source/Target Connection cards), the panel
  // drops to list+activate+rename+delete only, and each row activates
  // itself directly on click instead of loading into a nonexistent form.
  // Cross-kind rows (e.g. a "source" row shown on the "target" step) stay
  // visible in this mode and activate via `asKind` (see activate_saved_connection
  // in service.py) instead of being disabled/hidden.
  currentConn?: DbConnectionConfig | SalesforceConnectionConfig;
  onLoad?: (cfg: SavedConnectionOut) => void;
  onActivated: () => void;
  // 2026-09-02, requested by user: source/target connections saved from
  // ConnectDatabaseDialog land in a separate, cross-agent "DataConnection"
  // table (GET /projects/{id}/source-connections|target-connections) —
  // NOT the Data-Converter-specific ConverterSavedConnection table this
  // panel otherwise reads/writes. Without this, a connection made visible
  // via that dialog's own picker (e.g. "TX_CW_SRC") looked like it didn't
  // exist here, even though it's usable. Passing the same
  // onConnectExisting the sibling ConnectDatabaseDialog on that step uses
  // lets this panel also list and activate those rows. Omit for "crm" —
  // there's no equivalent shared cross-agent CRM connection list.
  // `label` — the shared row's display name/db_type·host, so the caller
  // can show something better than a generic "saved connection" string
  // once connected (the connect response itself doesn't carry host/name).
  onConnectExisting?: (
    connectionId: string,
    label?: string,
  ) => Promise<{ ok: boolean; error?: string }>;
}) {
  const hasForm = onLoad !== undefined;
  const [items, setItems] = useState<SavedConnectionOut[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [activatingId, setActivatingId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  // Editing a saved connection's credential — the recovery path for "this
  // saved connection's stored password could not be found" (an ephemeral
  // dev Vault reset, a rotated password, ...). Blank means "leave
  // unchanged"; only sent to the server if non-blank.
  const [editPassword, setEditPassword] = useState("");
  const [editClientSecret, setEditClientSecret] = useState("");
  // 2026-09-03, reported by user: a Salesforce INVALID_LOGIN from a
  // rotated/expired security token had no fix short of deleting and
  // re-adding the whole saved connection -- this panel's edit form could
  // only fix the password/client secret, never the token. crm-kind only.
  const [editSecurityToken, setEditSecurityToken] = useState("");
  // Workday's OAuth2 refresh_token grant credential -- same fix-a-stale-
  // secret path as editSecurityToken above. crm-kind only.
  const [editRefreshToken, setEditRefreshToken] = useState("");
  const [savingEditId, setSavingEditId] = useState<string | null>(null);
  // Transient confirmation after "Save Connection" — distinguishes "this
  // became a brand-new library entry" from "this exactly matched one
  // already saved, so I just switched to it" (create_saved_connection is
  // idempotent on an unchanged config). Without this, saving a genuinely
  // new connection and re-saving an unchanged one look identical to the
  // user, which is the root of "I don't see where multiple connections get
  // saved" confusion.
  const [savedFeedback, setSavedFeedback] = useState<"new" | "existing" | null>(
    null,
  );
  // The cross-agent "DataConnection" library (see onConnectExisting above) —
  // source/target only, fetched alongside `items` so both libraries show up
  // in one place instead of the user needing to know which of two separate
  // pickers a given saved connection lives in.
  const [sharedItems, setSharedItems] = useState<DBConfigOut[] | null>(null);
  const [activatingSharedId, setActivatingSharedId] = useState<string | null>(
    null,
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [res, shared] = await Promise.all([
        apiv2.converterListSavedConnections(projectId, kind),
        kind === "crm"
          ? Promise.resolve<DBConfigOut[]>([])
          : kind === "target"
            ? apiv2.listTargetConnections(projectId)
            : apiv2.listSourceConnections(projectId),
      ]);
      setItems(res);
      setSharedItems(shared);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Failed to load saved connections",
      );
    } finally {
      setLoading(false);
    }
  }, [projectId, kind]);

  useEffect(() => {
    load();
  }, [load]);

  const handleActivateShared = async (id: string, label?: string) => {
    if (!onConnectExisting) return;
    setActivatingSharedId(id);
    setError(null);
    try {
      const result = await onConnectExisting(id, label);
      if (!result.ok) {
        setError(result.error ?? "Failed to activate connection");
      }
      // Deliberately NOT calling onActivated() here — unlike handleActivate
      // above, onConnectExisting for a cross-agent shared row only tests
      // the connection (data_converter's /source|target/connect), it never
      // calls the backend's activate_saved_connection (which is what
      // actually rebinds admin/project_db_config's single slot). Calling
      // onActivated() used to re-fetch that slot via getSourceDB/
      // getTargetDB right after — which returns whichever connection was
      // bound there LAST, not necessarily this one — silently reverting
      // the picker back to a stale connection (e.g. re-selecting a
      // Databricks source kept showing Oracle). onConnectExisting's own
      // result already updated the caller's local connection state
      // correctly; nothing further to sync.
    } finally {
      setActivatingSharedId(null);
    }
  };

  // The one save action for this form: adds/reuses a library entry for the
  // current field values (create_saved_connection is idempotent — an
  // unchanged re-save returns the existing row rather than erroring or
  // duplicating it) and immediately activates it, so the connection is both
  // saved to the library and pushed into the project's single active slot
  // in one click — no separate "persist" action needed.
  //
  // currentConn's password/client_secret are "" (not omitted) whenever the
  // form is showing the "saved — leave blank to keep" placeholder — sending
  // that literal "" through create+activate would overwrite the project's
  // real stored secret with an empty one (activation treats any non-null
  // password, including "", as "here is the new password"). Strip blank
  // secrets before sending so an unchanged secret is genuinely omitted, not
  // sent as empty.
  const handleSaveCurrent = async () => {
    setSaving(true);
    setError(null);
    setSavedFeedback(null);
    const existingIds = new Set((items ?? []).map((i) => i.id));
    try {
      const body: Record<string, unknown> = { ...currentConn, kind };
      if (!body.password) delete body.password;
      if (!body.client_secret) delete body.client_secret;
      const row = await apiv2.converterCreateSavedConnection(
        projectId,
        body as Parameters<typeof apiv2.converterCreateSavedConnection>[1],
      );
      await apiv2.converterActivateSavedConnection(projectId, row.id);
      await load();
      onActivated();
      setSavedFeedback(existingIds.has(row.id) ? "existing" : "new");
      setTimeout(() => setSavedFeedback(null), 4000);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save connection");
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (id: string, asKind?: "source" | "target") => {
    setActivatingId(id);
    setError(null);
    try {
      await apiv2.converterActivateSavedConnection(projectId, id, asKind);
      await load();
      onActivated();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Failed to activate connection",
      );
    } finally {
      setActivatingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    setError(null);
    try {
      await apiv2.converterDeleteSavedConnection(projectId, id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to delete connection");
    }
  };

  // Saves the edit form's label/password/client_secret — any left blank is
  // omitted (left unchanged server-side), so fixing just the password
  // doesn't require re-typing the label too. A password/client_secret
  // change also re-activates the connection server-side (see the PUT
  // route), so onActivated() fires to let the parent step's "Connected"
  // status refresh immediately.
  const handleSaveEdit = async (id: string, originalLabel: string) => {
    const label = renameValue.trim();
    const password = editPassword.trim();
    const clientSecret = editClientSecret.trim();
    const securityToken = editSecurityToken.trim();
    const refreshToken = editRefreshToken.trim();
    setSavingEditId(id);
    setError(null);
    try {
      await apiv2.converterUpdateSavedConnection(projectId, id, {
        ...(label && label !== originalLabel ? { label } : {}),
        ...(password ? { password } : {}),
        ...(clientSecret ? { clientSecret } : {}),
        ...(securityToken ? { securityToken } : {}),
        ...(refreshToken ? { refreshToken } : {}),
      });
      setRenamingId(null);
      setEditPassword("");
      setEditClientSecret("");
      setEditSecurityToken("");
      setEditRefreshToken("");
      await load();
      if (password || clientSecret || securityToken || refreshToken)
        onActivated();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to update connection");
    } finally {
      setSavingEditId(null);
    }
  };

  return (
    <div className="p-4 bg-white/3 border border-white/8 rounded-xl space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-white/50 uppercase tracking-wide">
            Saved Connections
          </p>
          <p className="text-[11px] text-white/30">
            {hasForm
              ? "Each click below saves the connection above as its own entry — fill in a different host, database, or user first to build up more than one."
              : "Pick a previously-saved connection to use here."}
          </p>
        </div>
        {hasForm && (
          <button
            onClick={handleSaveCurrent}
            disabled={saving}
            className="ztd-btn ztd-btn-secondary ztd-btn-sm flex-shrink-0"
          >
            {saving ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Database size={12} />
            )}
            Save Connection
          </button>
        )}
      </div>

      {savedFeedback === "new" && (
        <p className="flex items-center gap-1.5 text-xs text-green-400">
          <CheckCircle2 size={12} /> Saved as a new connection — now active.
        </p>
      )}
      {savedFeedback === "existing" && (
        <p className="flex items-center gap-1.5 text-xs text-white/50">
          <CheckCircle2 size={12} /> This exactly matches a connection already
          saved below — switched to it instead of adding a duplicate.
        </p>
      )}

      {error && <p className="text-xs text-red-400">{error}</p>}

      {loading && items === null ? (
        <p className="text-xs text-white/30 italic">Loading…</p>
      ) : (!items || items.length === 0) &&
        (!sharedItems || sharedItems.length === 0) ? (
        // issue #623: compact single-line empty state instead of an
        // always-expanded full-width card with nothing to show.
        <p className="text-xs text-white/30 italic">
          {hasForm
            ? 'No saved connections yet — fill in the form above and click "Save Connection" to start building a library.'
            : "No saved connections yet."}
        </p>
      ) : (
        // 2026-09-08, issue #623: was a single-column vertical stack of
        // full-width bars (each row using only ~60% of its width for actual
        // content) — a project with several saved connections forced a lot
        // of scrolling. Two-column on wider screens roughly halves that.
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {items &&
            items.length === 0 &&
            sharedItems &&
            sharedItems.length > 0 && (
              <p className="text-xs text-white/30 italic sm:col-span-2">
                {hasForm
                  ? 'No connections saved from here yet — fill in the form above and click "Save Connection" to add one.'
                  : "None saved from this step yet — see below."}
              </p>
            )}
          {items?.map((c) =>
            renamingId === c.id ? (
              <div
                key={c.id}
                className="p-2.5 rounded-lg border border-brand-500/40 bg-brand-500/5 space-y-2 sm:col-span-2"
              >
                <div>
                  <label className="block text-[10px] font-medium text-white/40 uppercase tracking-wide mb-1">
                    Connection Name
                  </label>
                  <input
                    autoFocus
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSaveEdit(c.id, c.label);
                      if (e.key === "Escape") setRenamingId(null);
                    }}
                    className="w-full px-2 py-1 bg-surface-400/60 border border-white/10 rounded text-xs text-white focus:outline-none focus:border-brand-500/50"
                  />
                </div>
                <div
                  className={`grid ${kind === "crm" ? "grid-cols-3" : "grid-cols-1"} gap-2`}
                >
                  <div>
                    <label className="block text-[10px] font-medium text-white/40 uppercase tracking-wide mb-1">
                      Password
                    </label>
                    <input
                      type="password"
                      value={editPassword}
                      onChange={(e) => setEditPassword(e.target.value)}
                      placeholder="•••• stored — leave blank to keep"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSaveEdit(c.id, c.label);
                        if (e.key === "Escape") setRenamingId(null);
                      }}
                      className="w-full px-2 py-1 bg-surface-400/60 border border-white/10 rounded text-xs text-white focus:outline-none focus:border-brand-500/50"
                    />
                  </div>
                  {kind === "crm" && (
                    <div>
                      <label className="block text-[10px] font-medium text-white/40 uppercase tracking-wide mb-1">
                        Client Secret
                      </label>
                      <input
                        type="password"
                        value={editClientSecret}
                        onChange={(e) => setEditClientSecret(e.target.value)}
                        placeholder="•••• stored — leave blank to keep"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSaveEdit(c.id, c.label);
                          if (e.key === "Escape") setRenamingId(null);
                        }}
                        className="w-full px-2 py-1 bg-surface-400/60 border border-white/10 rounded text-xs text-white focus:outline-none focus:border-brand-500/50"
                      />
                    </div>
                  )}
                  {kind === "crm" && (
                    <div>
                      <label className="block text-[10px] font-medium text-white/40 uppercase tracking-wide mb-1">
                        Security Token
                      </label>
                      <input
                        type="password"
                        value={editSecurityToken}
                        onChange={(e) => setEditSecurityToken(e.target.value)}
                        placeholder="•••• stored — leave blank to keep"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSaveEdit(c.id, c.label);
                          if (e.key === "Escape") setRenamingId(null);
                        }}
                        className="w-full px-2 py-1 bg-surface-400/60 border border-white/10 rounded text-xs text-white focus:outline-none focus:border-brand-500/50"
                      />
                    </div>
                  )}
                  {kind === "crm" && (
                    <div>
                      <label className="block text-[10px] font-medium text-white/40 uppercase tracking-wide mb-1">
                        Refresh Token
                      </label>
                      <input
                        type="password"
                        value={editRefreshToken}
                        onChange={(e) => setEditRefreshToken(e.target.value)}
                        placeholder="•••• stored — leave blank to keep"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSaveEdit(c.id, c.label);
                          if (e.key === "Escape") setRenamingId(null);
                        }}
                        className="w-full px-2 py-1 bg-surface-400/60 border border-white/10 rounded text-xs text-white focus:outline-none focus:border-brand-500/50"
                      />
                    </div>
                  )}
                </div>
                <p className="text-[10px] text-white/30">
                  Leave a field blank to keep its current value. Setting a
                  password
                  {kind === "crm"
                    ? ", client secret, security token, or refresh token"
                    : ""}{" "}
                  also reactivates this connection so the fix takes effect
                  immediately.
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleSaveEdit(c.id, c.label)}
                    disabled={savingEditId === c.id}
                    className="ztd-btn ztd-btn-primary ztd-btn-sm"
                  >
                    {savingEditId === c.id ? (
                      <Loader2 size={11} className="animate-spin" />
                    ) : (
                      "Save"
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setRenamingId(null);
                      setEditPassword("");
                      setEditClientSecret("");
                      setEditSecurityToken("");
                      setEditRefreshToken("");
                    }}
                    disabled={savingEditId === c.id}
                    className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div
                key={c.id}
                className={`flex items-center gap-2 p-2 rounded-lg border transition ${
                  c.is_active
                    ? "border-brand-500/40 bg-brand-500/10"
                    : "border-white/8 bg-white/[0.02]"
                }`}
              >
                <button
                  onClick={() =>
                    hasForm
                      ? onLoad?.(c)
                      : handleActivate(
                          c.id,
                          c.kind !== kind
                            ? (kind as "source" | "target")
                            : undefined,
                        )
                  }
                  className="flex-1 min-w-0 text-left"
                  title={
                    hasForm
                      ? "Load into the form"
                      : c.kind !== kind
                        ? `Use this as ${kind}`
                        : "Use this connection"
                  }
                >
                  <span className="flex items-center gap-1.5 text-sm text-white/80 truncate">
                    {c.label}
                    {kind !== "crm" && c.kind !== kind && (
                      <span
                        className="text-[10px] text-white/40 font-medium flex-shrink-0 uppercase tracking-wide border border-white/15 rounded px-1"
                        title={
                          hasForm
                            ? `Saved as a ${c.kind} connection — click to load it here, then Save Connection to use it as ${kind}.`
                            : `Saved as a ${c.kind} connection — click to use it as ${kind} too.`
                        }
                      >
                        {c.kind}
                      </span>
                    )}
                    {c.is_active && (
                      <span className="flex items-center gap-0.5 text-[10px] text-brand-400 font-medium flex-shrink-0">
                        <CheckCircle2 size={10} /> ACTIVE
                      </span>
                    )}
                  </span>
                  <span className="block text-[11px] text-white/30 truncate">
                    {kind === "crm" ? (
                      <>
                        {c.target_type}
                        {c.instance_url ? ` · ${c.instance_url}` : ""}
                      </>
                    ) : (
                      <>
                        {c.db_type}
                        {c.host
                          ? ` · ${c.host}`
                          : c.bridge_label
                            ? ` · ${c.bridge_label}`
                            : ""}
                        {c.database ? `/${c.database}` : ""}
                      </>
                    )}
                  </span>
                  {kind !== "crm" && savedConnectionDetail(c) && (
                    <span className="block text-[11px] text-white/25 truncate">
                      {savedConnectionDetail(c)}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => {
                    setRenamingId(c.id);
                    setRenameValue(c.label);
                    setEditPassword("");
                    setEditClientSecret("");
                    setEditSecurityToken("");
                    setEditRefreshToken("");
                  }}
                  className="p-1 rounded text-white/30 hover:text-white hover:bg-white/10 transition flex-shrink-0"
                  title="Edit name or fix a stored credential"
                >
                  <Pencil size={12} />
                </button>
                {c.kind === kind ? (
                  <button
                    onClick={() => handleActivate(c.id)}
                    disabled={activatingId === c.id || c.is_active}
                    className={`px-2 py-1 rounded-md text-[11px] font-medium border transition flex-shrink-0 ${
                      c.is_active
                        ? "border-brand-500/30 text-brand-400 cursor-default"
                        : "border-white/10 text-white/60 hover:text-white hover:bg-white/10"
                    }`}
                  >
                    {activatingId === c.id ? (
                      <Loader2 size={11} className="animate-spin" />
                    ) : c.is_active ? (
                      "In Use"
                    ) : (
                      "Use This"
                    )}
                  </button>
                ) : hasForm ? (
                  <button
                    disabled
                    title={`Saved as ${c.kind} — click the connection to load it into the form above, then Save Connection to use it as ${kind}.`}
                    className="px-2 py-1 rounded-md text-[11px] font-medium border border-white/5 text-white/25 cursor-default flex-shrink-0"
                  >
                    Load first
                  </button>
                ) : (
                  <button
                    onClick={() =>
                      handleActivate(c.id, kind as "source" | "target")
                    }
                    disabled={activatingId === c.id}
                    title={`Push this ${c.kind} connection's credentials into the ${kind} slot too — same server, different role.`}
                    className="px-2 py-1 rounded-md text-[11px] font-medium border border-white/10 text-white/60 hover:text-white hover:bg-white/10 transition flex-shrink-0"
                  >
                    {activatingId === c.id ? (
                      <Loader2 size={11} className="animate-spin" />
                    ) : (
                      `Use as ${kind}`
                    )}
                  </button>
                )}
                <button
                  onClick={() => handleDelete(c.id)}
                  className="p-1 rounded text-white/30 hover:text-red-400 hover:bg-red-400/10 transition flex-shrink-0"
                  title="Delete"
                >
                  <X size={12} />
                </button>
              </div>
            ),
          )}

          {sharedItems && sharedItems.length > 0 && (
            <div className="sm:col-span-2 pt-1.5">
              <p className="text-[10px] font-medium text-white/30 uppercase tracking-wide mb-1.5">
                Also connected elsewhere in this project
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {sharedItems.map((c) => (
                  <div
                    key={c.id}
                    className="flex items-center gap-2 p-2 rounded-lg border border-white/8 bg-white/[0.02]"
                  >
                    <div className="flex-1 min-w-0 text-left">
                      <span className="block text-sm text-white/80 truncate">
                        {savedConnectionTitle(c)}
                      </span>
                      <span className="block text-[11px] text-white/30 truncate">
                        {savedConnectionEndpoint(c)}
                      </span>
                      {savedConnectionDetail(c) && (
                        <span className="block text-[11px] text-white/25 truncate">
                          {savedConnectionDetail(c)}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() =>
                        handleActivateShared(c.id, savedConnectionTitle(c))
                      }
                      disabled={
                        activatingSharedId === c.id || !onConnectExisting
                      }
                      title="Saved from another agent's Connect Database dialog for this project — use it here too."
                      className="px-2 py-1 rounded-md text-[11px] font-medium border border-white/10 text-white/60 hover:text-white hover:bg-white/10 transition flex-shrink-0"
                    >
                      {activatingSharedId === c.id ? (
                        <Loader2 size={11} className="animate-spin" />
                      ) : (
                        "Use This"
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
